'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { CompactTable, TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { flexRender } from '@tanstack/react-table';
import { memo } from 'react';
import type { DashboardEarnPositionsFilters } from '@/helpers/filter/table';
import type { UserEarnPositionFacets } from '@/hooks/positions/userEarnPositionsQuery';
import { InternalLink } from '@/components/common/InternalLink';
import { getVaultUrl } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { EarnPositionsTableFeatures } from './config/DashboardEarnPositionsTable.columns';
import type { DashboardEarnPositionsTableRow, EarnPositions } from './config/DashboardEarnPositionsTable.types';
import DashboardPositionsTable, { DATA_CELL_CONTENT_CLASS, PINNED_CELL_CONTENT_CLASS, POSITION_MANAGER_COLUMN_ID } from '../DashboardPositionsTable';
import DashboardEarnPositionsFiltersComponent from './DashboardEarnPositionsFilters';
interface DashboardEarnPositionsDesktopViewProps {
  table: ReactTable<EarnPositionsTableFeatures, DashboardEarnPositionsTableRow>;
  filters?: DashboardEarnPositionsFilters;
  onFiltersChange?: (filters: DashboardEarnPositionsFilters) => void;
  positionChainIds?: ChainId[];
  columnLabels: Record<string, string>;
  earnPositions: EarnPositions[];
  loading: boolean;
  disabled?: boolean;
  facets?: UserEarnPositionFacets;
}
const DashboardEarnPositionsDesktopView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  columnLabels,
  earnPositions,
  loading,
  disabled,
  facets
}: DashboardEarnPositionsDesktopViewProps) => {
  // 'use no memo': `table` keeps one reference while its rows change, so a compiled component
  // would cache the rows of an earlier filter.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const dataRows = table.getRowModel().rows;
  const hasFilteredResults = dataRows.length > 0;
  return <div data-testid="dashboard-earn-positions-card" className="border-border bg-card flex min-h-0 flex-col rounded-[20px] border-[0.5px] shadow-md dark:shadow-none">
      <div className="bg-card sticky z-1 overflow-hidden rounded-t-[20px]">
        <div className="p-md box-border content-center rounded-t-[20px]">
          <DashboardEarnPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
        </div>
      </div>

      {!hasFilteredResults ? <TableNoFilterResults withBorder type="vaults" count={earnPositions.length} clearFilters={() => onFiltersChange?.({
      assetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <DashboardPositionsTable columns={table.getVisibleLeafColumns()} data-testid="dashboard-earn-positions-table">
          <CompactTable.Head headerGroups={table.getHeaderGroups()} columnLabels={columnLabels} />
          <CompactTable.Body className="[&>tr:last-child_div]:border-b-0">
            {dataRows.map(row => {
          const vaultUrl = getVaultUrl({
            identifier: InterfaceChainUtils.getIdentifierById(row.original.vault.asset.chain.id),
            vaultAddress: row.original.vault.address,
            vaultName: row.original.vault.name,
            hash: isSelf ? 'yourPosition' : undefined
          });
          return <CompactTable.Row key={row.id} data-testid={`vault-${row.original.vault.address}`}>
                  {row.getVisibleCells().map(cell => <CompactTable.Cell key={cell.id}>
                      <InternalLink href={vaultUrl} prefetch="on-hover-debounced">
                        <CompactTable.CellContent className={cell.column.id === POSITION_MANAGER_COLUMN_ID ? PINNED_CELL_CONTENT_CLASS : DATA_CELL_CONTENT_CLASS}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </CompactTable.CellContent>
                      </InternalLink>
                    </CompactTable.Cell>)}
                </CompactTable.Row>;
        })}
          </CompactTable.Body>
        </DashboardPositionsTable>}
    </div>;
};
export default memo(DashboardEarnPositionsDesktopView);